/**
 * Energy performance: the EPC summary (ratings, scores, lodgement, heating) for
 * the side panel, and its details (current vs potential, certificate evidence,
 * recorded building features, what to verify). Scottish analytical snapshots
 * carry their notice; certificate validity is not claimed for them.
 */
import type { EpcAssessment } from '../../../api/types'
import { safeExternalHttpUrl, type JsonRecord } from '../../../api/diligence'
import { date, label as titleCase, plain } from './format'
import { EpcPair, EpcRating, Metric, Metrics, Note } from './ui'

export const scottishEpcAnalyticalNotice = 'Scottish EPC analytical snapshot; certificate validity has not been verified.'

const asRecord = (value: unknown): JsonRecord | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : null
const asRecords = (value: unknown): JsonRecord[] => Array.isArray(value) ? value.map(asRecord).filter((item): item is JsonRecord => item !== null) : []

export function isScottishEpcAnalyticalSnapshot(assessment: EpcAssessment | undefined): boolean {
  const metadata = asRecord(assessment?.metadata)
  return metadata?.certificateValidityVerified !== true && (metadata?.sourceProvider === 'scottish_epc_certificates' || assessment?.sourceName === 'Scottish Government domestic EPC analytical snapshot')
}

/** Recorded certificate features with a description, in source order. */
export function epcFeatures(assessment: EpcAssessment | undefined): Array<{ name: string; description: string }> {
  const record = asRecord(asRecord(assessment?.metadata)?.features)
  return record ? Object.entries(record).flatMap(([name, value]) => {
    const first = asRecords(value)[0]
    const description = typeof first?.description === 'string' && first.description.trim() ? first.description : null
    return description ? [{ name, description }] : []
  }) : []
}

export function epcFloorArea(assessment: EpcAssessment | undefined): number | null {
  const value = asRecord(assessment?.metadata)?.totalFloorAreaSqm
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

const FEATURE_LABELS: Record<string, string> = {
  'mainheat-description': 'Main heating', 'mainheatcont-description': 'Heating controls', 'hot-water-description': 'Hot water', 'walls-description': 'Walls', 'roof-description': 'Roof',
  'floor-description': 'Floor', 'windows-description': 'Windows', 'lighting-description': 'Lighting', 'secondaryheat-description': 'Secondary heating',
}
const featureLabel = (name: string) => FEATURE_LABELS[name] ?? titleCase(name.replace(/-description$/, '').replaceAll('-', '_'))

export function EnergySummary({ assessment }: { assessment: EpcAssessment | undefined }) {
  if (!assessment) return <Note>No EPC assessment is available.</Note>
  const analytical = isScottishEpcAnalyticalSnapshot(assessment)
  const heating = [...new Set(epcFeatures(assessment).filter(feature => ['mainheat-description', 'mainHeating'].includes(feature.name)).map(feature => feature.description))].join(' · ')
  return (
    <div className="dil-energy">
      <EpcPair current={assessment.currentRating} potential={assessment.potentialRating} />
      {analytical ? <Note>{scottishEpcAnalyticalNotice}</Note> : null}
      <dl className="kv"><dt>Current score</dt><dd>{plain(assessment.currentScore)}</dd><dt>Potential score</dt><dd>{plain(assessment.potentialScore)}</dd><dt>Lodged</dt><dd>{date(assessment.lodgementDate)}</dd></dl>
      <Note>{heating || (analytical ? 'See assessed features and improvement potential in Details.' : 'See certificate features and improvement potential in Details.')}</Note>
    </div>
  )
}

export function EnergyDetails({ assessment }: { assessment: EpcAssessment | undefined }) {
  if (!assessment) return <p className="t-muted">No EPC assessment is available.</p>
  const rows = [
    { key: 'current', label: 'Current assessment', score: assessment.currentScore, rating: assessment.currentRating },
    { key: 'potential', label: 'Potential after improvements', score: assessment.potentialScore, rating: assessment.potentialRating },
  ]
  const scale = Math.max(100, ...rows.map(row => row.score ?? 0))
  const source = safeExternalHttpUrl(assessment.sourceUrl ?? null)
  const analytical = isScottishEpcAnalyticalSnapshot(assessment)
  const floorArea = epcFloorArea(assessment)
  const features = epcFeatures(assessment)
  return (
    <div className="dil-detail">
      {analytical ? <Note>{scottishEpcAnalyticalNotice}</Note> : null}
      <section className="dil-sec" aria-label="Current and potential energy performance">
        <header><h4>Current and potential performance</h4><p>{analytical ? 'The analytical snapshot compares the assessed dwelling with its reported potential after recommended improvements.' : 'The certificate compares the assessed dwelling with its potential after the recommended improvements.'}</p></header>
        <dl className="dil-epc-rows">{rows.map(row => (
          <div key={row.key}>
            <dt>{row.label}</dt>
            <dd>
              <div className="dil-epc-value"><EpcRating rating={row.rating} label={`${row.key === 'current' ? 'Current' : 'Potential'} EPC`} /><b>{plain(row.score)}</b><span>energy-efficiency score</span></div>
              <span className="dil-track" aria-hidden="true">{row.score != null ? <i style={{ width: `${Math.max(0, row.score) / scale * 100}%` }} /> : null}</span>
            </dd>
          </div>
        ))}</dl>
        <div className="dil-scale" aria-hidden="true"><span>0</span><span>Higher score = more efficient</span><span>{scale}</span></div>
      </section>
      <section className="dil-sec" aria-label={analytical ? 'Analytical EPC evidence' : 'Certificate evidence'}>
        <header><h4>{analytical ? 'Analytical EPC evidence' : 'Certificate evidence'}</h4></header>
        <Metrics className="is-three">
          <Metric label="Lodged" value={date(assessment.lodgementDate)} />
          <Metric label="Reported floor area" value={floorArea == null ? 'Unknown' : `${plain(floorArea)} m²`} />
          <Metric label="Source" value={source ? <a href={source} target="_blank" rel="noopener noreferrer">{assessment.sourceName ?? (analytical ? 'Open analytical source' : 'Open certificate source')}</a> : assessment.sourceName ?? 'Unknown'} />
        </Metrics>
        <Note>{analytical ? 'The assessment may predate later alterations. Its reported floor area can differ from the resolved property area used by the works estimate.' : 'The certificate may predate later alterations. Its floor area can differ from the resolved property area used by the works estimate.'}</Note>
      </section>
      {features.length ? <section className="dil-sec" aria-label="Recorded building features"><header><h4>Recorded building features</h4></header><dl className="dil-pairs is-wide">{features.map(feature => <div key={feature.name}><dt>{featureLabel(feature.name)}</dt><dd>{feature.description}</dd></div>)}</dl></section> : null}
      <section className="dil-sec" aria-label="What to verify">
        <header><h4>What to verify</h4></header>
        <ul className="dil-list">
          <li>{analytical ? 'Verify the current certificate separately, and check the assessment date, current layout and subsequent upgrades.' : 'Check the certificate date, current layout and any upgrades carried out since assessment.'}</li>
          <li>{analytical ? 'Review the reported recommendations and obtain costs for feasible improvements.' : 'Review the certificate’s recommendations and obtain costs for feasible improvements.'}</li>
          <li>The potential rating is conditional. It does not establish a current energy bill, a works quotation or compliance.</li>
        </ul>
      </section>
    </div>
  )
}
